import { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import { Button } from 'src/components/button';
import { checkIsEngBuilding } from 'src/utils/is-english-building';

import { Building } from '../../shared/types/building';
import { validateEmail } from '../../utils/validators/validate-email';
import { Agreement } from '../agreements/agreement';
import { AgreementContent } from '../agreements/agreements-content';
import { BookingTermsContent } from '../agreements/booking-terms-content';
import { EmailValidationState } from '../agreements/enum/EmailValidationState';
import { styles } from '../agreements/styles';
import { AgreementType } from '../agreements/types';
import { AuthContext } from '../../providers/auth-context';
import { routes } from '../../constants/routes';
import { useUpdateDualOccupantDetails } from '../../lib/hooks/use-post-update-dual-occupant-details';

import { OccupantAgreementsView } from './occupant-agreements-view';

export const OccupantAgreementsContainer: FC<{
    building: Building;
    vitaRef: string;
    handleBackButton: () => void;
    academicYear: string;
}> = ({ building, vitaRef, handleBackButton, academicYear }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const userState = useContext(AuthContext);

    const [termsCheckboxVal, setTermsCheckboxVal] = useState(false);
    const [licenceCheckboxVal, setLicenceCheckboxVal] = useState(false);
    const [termsEmailAddress, setTermsEmailAddress] = useState('');
    const [licenceEmailAddress, setLicenceEmailAddress] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [currentAgreement, setCurrentAgreement] = useState<AgreementType>(AgreementType.Terms);
    const [termsEmailValidationState, setTermsEmailValidationState] = useState(
        EmailValidationState.VALID
    );
    const [licenceEmailValidationState, setLicenceEmailValidationState] = useState(
        EmailValidationState.VALID
    );

    const {
        mutate: updateDualOccupantDetails,
        isPending: isUpdatingDualOccupantDetails,
        error: errorUpdatingDualOccupantDetails,
    } = useUpdateDualOccupantDetails();

    useEffect(() => {
        window.dataLayer.push({
            event: 'go_to_agreements',
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    if (!userState.loggedIn) return <Navigate to={routes.failure.index} />;

    const { user } = userState.session;

    const handleEmail = (
        value: string,
        userEmail: string | undefined,
        setValue: (v: string) => void,
        setState: (s: EmailValidationState) => void
    ) => {
        const email = value.trim().toLowerCase();
        setValue(email);

        if (!email.length) {
            setState(EmailValidationState.VALID);
            return;
        }

        if (!validateEmail(email)) {
            setState(EmailValidationState.INVALID_EMAIL);
            return;
        }

        if (!userEmail || email !== userEmail) {
            setState(EmailValidationState.MISMATCHED_EMAIL);
            return;
        }

        setState(EmailValidationState.VALID);
    };

    const isEnglishBuilding = checkIsEngBuilding(building);

    const isEmailValidTerms = termsEmailValidationState === EmailValidationState.VALID;
    const isEmailValidLicence = licenceEmailValidationState === EmailValidationState.VALID;

    const isBookingTermsSigned = termsCheckboxVal && !!termsEmailAddress && isEmailValidTerms;
    const isLicenceAgreementSigned =
        licenceCheckboxVal && !!licenceEmailAddress && isEmailValidLicence;

    const isButtonDisabled = !isBookingTermsSigned || !isLicenceAgreementSigned;

    const handleSubmit = () => {
        if (!isBookingTermsSigned || !isLicenceAgreementSigned) return;

        updateDualOccupantDetails(vitaRef, { onSuccess: () => navigate(routes.success.index) });
    };

    const openDrawer = (type: AgreementType) => {
        setCurrentAgreement(type);
        setDrawerOpen(true);
    };

    const closeDrawer = () => setDrawerOpen(false);

    const goToNextDrawer = () => {
        setCurrentAgreement(AgreementType.Licence);
    };

    const skipDrawer = () => setDrawerOpen(false);

    const emailHandler =
        (setValue: (v: string) => void, setState: (s: EmailValidationState) => void) =>
        (e: ChangeEvent<HTMLInputElement>) =>
            handleEmail(e.target.value, user.email, setValue, setState);

    const handleTermsEmail = emailHandler(setTermsEmailAddress, setTermsEmailValidationState);
    const handleLicenceEmail = emailHandler(setLicenceEmailAddress, setLicenceEmailValidationState);

    const bookingTermsAndConditions = (
        <Agreement
            checkboxVal={termsCheckboxVal}
            emailAddress={termsEmailAddress}
            handleEmailChange={handleTermsEmail}
            handleCheckboxChange={(e: ChangeEvent<HTMLInputElement>) =>
                setTermsCheckboxVal(e.target.checked)
            }
            emailValidationState={termsEmailValidationState}
            title="agreements_booking_terms_and_conditions_title"
            id="terms-email-input"
            subtitle="agreements_please_read_t&c"
            content={<BookingTermsContent building={building} />}
            checkboxContent="agreements_booking_terms_and_conditions_checkbox_content"
            emailContent="agreements_booking_terms_and_conditions_email_content"
            submitButton={
                <Button
                    variant="contained"
                    onClick={goToNextDrawer}
                    css={styles.cta}
                    disabled={!isBookingTermsSigned}
                >
                    {t('agreement_next_agreement')}
                </Button>
            }
            name="terms"
        />
    );

    const licenceAgreement = (
        <Agreement
            checkboxVal={licenceCheckboxVal}
            emailAddress={licenceEmailAddress}
            handleEmailChange={handleLicenceEmail}
            handleCheckboxChange={(e: ChangeEvent<HTMLInputElement>) =>
                setLicenceCheckboxVal(e.target.checked)
            }
            emailValidationState={licenceEmailValidationState}
            title={
                isEnglishBuilding
                    ? 'agreements_common_law_tenancy_title'
                    : 'agreements_licence_agreement_title'
            }
            id="licence-email-input"
            subtitle={
                isEnglishBuilding
                    ? 'agreements_please_read_common_law_tenancy'
                    : 'agreements_please_read_licence_agreement'
            }
            content={<AgreementContent building={building} />}
            checkboxContent={
                isEnglishBuilding
                    ? 'agreements_common_law_tenancy_checkbox_content'
                    : 'agreements_licence_agreement_checkbox_content'
            }
            emailContent={
                isEnglishBuilding
                    ? 'agreements_common_law_tenancy_email_content'
                    : 'agreements_licence_agreement_email_content'
            }
            submitButton={
                <Button
                    variant="contained"
                    onClick={skipDrawer}
                    css={styles.cta}
                    disabled={!isLicenceAgreementSigned}
                >
                    {t('agreements_submit')}
                </Button>
            }
            name="licence-agreement"
        />
    );

    return (
        <OccupantAgreementsView
            onGoBack={handleBackButton}
            hideBackButton={false}
            loading={isUpdatingDualOccupantDetails}
            error={errorUpdatingDualOccupantDetails}
            isButtonDisabled={isButtonDisabled}
            onSubmit={handleSubmit}
            terms={{
                checkboxVal: termsCheckboxVal,
                email: termsEmailAddress,
                validation: termsEmailValidationState,
                onCheckbox: setTermsCheckboxVal,
                onEmail: handleTermsEmail,
                isValid: isEmailValidTerms,
            }}
            licence={{
                checkboxVal: licenceCheckboxVal,
                email: licenceEmailAddress,
                validation: licenceEmailValidationState,
                onCheckbox: setLicenceCheckboxVal,
                onEmail: handleLicenceEmail,
                isValid: isEmailValidLicence,
            }}
            drawer={{
                open: drawerOpen,
                onClose: closeDrawer,
                current: currentAgreement,
                agreement: currentAgreement,
                total: 2,
            }}
            agreements={{
                terms: bookingTermsAndConditions,
                licence: licenceAgreement,
            }}
            showTermsDrawer={() => openDrawer(AgreementType.Terms)}
            showLicenceDrawer={() => openDrawer(AgreementType.Licence)}
            academicYear={academicYear}
        />
    );
};
